import { useApolloClient, useMutation, useQuery } from "@apollo/client";
import { Breadcrumb, Button, Dropdown, Icon, Tag, Toggle } from "pepsico-ds";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ToastNotification from "../../components/common/toastNotification/ToastNotification";
import JsonFormsWrapper from "../../components/jsonForms/jsonFormsWrapper/JsonFormsWrapper";
import { createKeywordGroup } from "../../graphql/mutations/createKeywordGroup";
import { deleteKeywordGroup } from "../../graphql/mutations/deleteKeywordGroup";
import { updateKeywordGroup } from "../../graphql/mutations/updateKeywordGroup";
import { listProducts } from "../../graphql/queries/listProducts";
import { loadKeywordGroupById } from "../../graphql/queries/loadKeywordGroupById";
import { useProductGroups } from "../../hooks/useProductGroups";
import { useToast } from "../../hooks/useToast";
import KeywordGroupEditModal from "./KeywordGroupEditModal";
import { productFormSchema, productInfoUISchema } from "./productFormSchema";
import "./productManagement.scss";

const PRODUCT_TYPE = "PRODUCT";
const KEYWORD_GROUP_TYPE = "PRODUCT_KEYWORD_GROUP";

const generateSlug = (name) =>
  (name || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

const initialFormData = {
  name: "",
  slug: "",
  imageUrl: null,
  keywords: [],
  keywordsGroupIds: [],
  metadata: {
    sku: "",
  },
};

export default function ProductDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const client = useApolloClient();
  const isEditMode = Boolean(id);
  const { toast, showToast, closeToast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [isFormReady, setIsFormReady] = useState(!isEditMode);

  // Active/Inactive toggle
  const [isActive, setIsActive] = useState(true);

  // Product groups (PRODUCT_GROUP type)
  const { productGroups, productGroupMap } = useProductGroups();
  const [selectedProductGroups, setSelectedProductGroups] = useState([]);

  // Keyword groups (PRODUCT_KEYWORD_GROUP type) attached to this product
  const [keywordGroups, setKeywordGroups] = useState([]);
  const [keywordGroupsLoading, setKeywordGroupsLoading] = useState(false);
  const [showKeywordModal, setShowKeywordModal] = useState(false);
  const [editingKeywordGroup, setEditingKeywordGroup] = useState(null);

  const [executeCreateKeyword] = useMutation(createKeywordGroup);
  const [executeUpdateKeyword] = useMutation(updateKeywordGroup);
  const [executeDeleteKeyword] = useMutation(deleteKeywordGroup);

  // Fetch existing product data for edit mode
  const { data: productsData, loading: productsLoading } = useQuery(listProducts, {
    fetchPolicy: "network-only",
    skip: !isEditMode,
  });

  // Set form data from product on edit
  useEffect(() => {
    if (!isEditMode) {
      setFormData(initialFormData);
      setKeywordGroups([]);
      setSelectedProductGroups([]);
      setIsActive(true);
      setIsFormReady(true);
      return;
    }
    setIsFormReady(false);
    if (productsData?.listKeywordGroups) {
      const product = productsData.listKeywordGroups.find((p) => p.id === id);
      if (product) {
        const meta =
          typeof product.metadata === "string"
            ? JSON.parse(product.metadata)
            : product.metadata || {};
        setFormData({
          id: product.id,
          name: product.name || "",
          slug: product.slug || generateSlug(product.name || ""),
          imageUrl: product.imageUrl || null,
          keywords: product.keywords || [],
          keywordsGroupIds: product.keywordsGroupIds || [],
          metadata: {
            sku: meta.sku || "",
          },
        });
        setIsActive(!product.deleted);
        setIsFormReady(true);
      }
    }
  }, [isEditMode, productsData, id]);

  // Once product data and product group map are loaded, set the selected product groups
  useEffect(() => {
    if (!isEditMode || !formData.keywordsGroupIds?.length) return;
    if (Object.keys(productGroupMap).length === 0) return;

    const selected = formData.keywordsGroupIds
      .filter((gid) => productGroupMap[gid])
      .map((gid) => ({ id: gid, displayText: productGroupMap[gid] }));

    setSelectedProductGroups(selected);
  }, [isEditMode, formData.keywordsGroupIds, productGroupMap]);

  // Fetch keyword groups (PRODUCT_KEYWORD_GROUP only) individually after product data loads
  useEffect(() => {
    if (!isEditMode || !productsData?.listKeywordGroups) return;

    const product = productsData.listKeywordGroups.find((p) => p.id === id);
    const groupIds = product?.keywordsGroupIds;
    if (!groupIds || groupIds.length === 0) {
      setKeywordGroups([]);
      return;
    }

    let cancelled = false;
    setKeywordGroupsLoading(true);

    const fetchKeywordGroups = async () => {
      try {
        const results = await Promise.all(
          groupIds.map((groupId) =>
            client.query({
              query: loadKeywordGroupById,
              variables: { id: groupId },
              fetchPolicy: "network-only",
            })
          )
        );

        if (cancelled) return;

        // Only keep PRODUCT_KEYWORD_GROUP type groups
        const loadedGroups = results
          .map((result) => result?.data?.loadKeyWordGroupById)
          .filter(Boolean)
          .filter((kg) => kg.type === KEYWORD_GROUP_TYPE)
          .map((kg) => ({
            id: kg.id,
            name: kg.name,
            keywords: kg.keywords || [],
            isExisting: true,
          }));

        setKeywordGroups(loadedGroups);
      } catch (err) {
        if (!cancelled) {
          console.error("Failed to load keyword groups:", err);
        }
      } finally {
        if (!cancelled) {
          setKeywordGroupsLoading(false);
        }
      }
    };

    fetchKeywordGroups();

    return () => {
      cancelled = true;
    };
  }, [isEditMode, productsData, id, client]);

  const handleFormChange = useCallback((data) => {
    setFormData((prev) => {
      const updated = {
        ...prev,
        ...data,
        id: prev.id,
        keywordsGroupIds: prev.keywordsGroupIds,
        metadata: {
          ...prev.metadata,
          ...(data.metadata || {}),
        },
      };
      const currentName = data.name !== undefined ? data.name : prev.name;
      updated.slug = generateSlug(currentName);
      return updated;
    });
  }, []);

  const handleProductGroupChange = useCallback((selected) => {
    setSelectedProductGroups(selected || []);
  }, []);

  const handleCancel = () => {
    navigate("/product-management");
  };

  const jsonFormsConfig = useMemo(() => ({ showValidation }), [showValidation]);

  // Keyword Group Handlers
  const handleAddKeywordGroup = () => {
    setEditingKeywordGroup(null);
    setShowKeywordModal(true);
  };

  const handleEditKeywordGroup = (keywordGroup) => {
    setEditingKeywordGroup(keywordGroup);
    setShowKeywordModal(true);
  };

  const handleDeleteKeywordGroup = async (keywordGroup) => {
    if (keywordGroup.isExisting && keywordGroup.id) {
      try {
        await executeDeleteKeyword({ variables: { id: keywordGroup.id } });
        setKeywordGroups((prev) => prev.filter((kg) => kg.id !== keywordGroup.id));
        setFormData((prev) => ({
          ...prev,
          keywordsGroupIds: (prev.keywordsGroupIds || []).filter(
            (gid) => gid !== keywordGroup.id
          ),
        }));
        showToast(
          "success",
          "Keyword Group Deleted",
          `"${keywordGroup.name}" has been removed`
        );
      } catch (error) {
        showToast(
          "error",
          "Delete Failed",
          error.message || "Could not delete the keyword group"
        );
      }
    } else {
      setKeywordGroups((prev) =>
        prev.filter((kg) => kg.tempId !== keywordGroup.tempId)
      );
    }
  };

  const handleSaveKeywordGroup = async (keywordGroupData) => {
    try {
      if (keywordGroupData.isExisting && keywordGroupData.id) {
        await executeUpdateKeyword({
          variables: {
            id: keywordGroupData.id,
            input: {
              name: keywordGroupData.name,
              slug: generateSlug(keywordGroupData.name),
              type: KEYWORD_GROUP_TYPE,
              keywords: keywordGroupData.keywords,
            },
          },
        });
        setKeywordGroups((prev) =>
          prev.map((kg) =>
            kg.id === keywordGroupData.id
              ? {
                  ...kg,
                  name: keywordGroupData.name,
                  keywords: keywordGroupData.keywords,
                }
              : kg
          )
        );
        showToast(
          "success",
          "Keyword Group Updated",
          `"${keywordGroupData.name}" has been updated`
        );
      } else {
        const result = await executeCreateKeyword({
          variables: {
            input: {
              name: keywordGroupData.name,
              slug: generateSlug(keywordGroupData.name),
              type: KEYWORD_GROUP_TYPE,
              keywords: keywordGroupData.keywords,
            },
          },
        });
        const newId = result?.data?.createKeywordGroup?.id;
        if (newId) {
          setKeywordGroups((prev) => {
            const filtered = prev.filter(
              (kg) => kg.tempId !== keywordGroupData.tempId
            );
            return [
              ...filtered,
              {
                id: newId,
                name: keywordGroupData.name,
                keywords: keywordGroupData.keywords,
                isExisting: true,
              },
            ];
          });
        }
        showToast(
          "success",
          "Keyword Group Created",
          `"${keywordGroupData.name}" has been created`
        );
      }
    } catch (error) {
      showToast("error", "Error", error.message || "Failed to save keyword group");
    }
    setShowKeywordModal(false);
    setEditingKeywordGroup(null);
  };

  const handleSave = async () => {
    if (!formData.name?.trim()) {
      setShowValidation(true);
      showToast("error", "Validation Error", "Product name is required");
      return;
    }
    if (!formData.metadata?.sku?.trim()) {
      setShowValidation(true);
      showToast("error", "Validation Error", "EAN/UPC is required");
      return;
    }

    setIsSubmitting(true);

    try {
      const allKeywords = keywordGroups.reduce((acc, kg) => {
        return [...acc, ...(kg.keywords || [])];
      }, []);

      // Keyword group IDs (PRODUCT_KEYWORD_GROUP type)
      const keywordGroupIds = keywordGroups
        .filter((kg) => kg.isExisting && kg.id)
        .map((kg) => kg.id);

      // Product group IDs from dropdown selection
      const productGroupIds = selectedProductGroups.map((g) => g.id);

      // Merge both into keywordsGroupIds
      const keywordsGroupIds = [...productGroupIds, ...keywordGroupIds];

      const payload = {
        name: formData.name,
        slug: generateSlug(formData.name),
        imageUrl: formData.imageUrl || null,
        type: PRODUCT_TYPE,
        keywords: allKeywords.length > 0 ? allKeywords : [formData.name],
        keywordsGroupIds,
        metadata: {
          sku: formData.metadata.sku,
          name: formData.name,
        },
      };

      if (isEditMode && formData.id) {
        await executeUpdateKeyword({
          variables: { id: formData.id, input: payload },
        });

        // Handle active/inactive via deleteKeywordGroup (soft delete)
        const product = productsData?.listKeywordGroups?.find((p) => p.id === id);
        const wasActive = !product?.deleted;
        if (wasActive && !isActive) {
          // Deactivate: soft delete
          await executeDeleteKeyword({ variables: { id: formData.id } });
        }

        showToast(
          "success",
          "Product Updated",
          "Product has been updated successfully"
        );
      } else {
        const result = await executeCreateKeyword({
          variables: { input: payload },
        });

        // If creating as inactive, soft delete right after
        if (!isActive) {
          const newId = result?.data?.createKeywordGroup?.id;
          if (newId) {
            await executeDeleteKeyword({ variables: { id: newId } });
          }
        }

        showToast(
          "success",
          "Product Created",
          "Product has been created successfully"
        );
      }

      setIsSubmitting(false);
      setTimeout(() => navigate("/product-management"), 1200);
    } catch (error) {
      setIsSubmitting(false);
      showToast(
        "error",
        "Error",
        error.message || "An error occurred while saving the product"
      );
    }
  };

  const breadcrumbs = [
    { label: "Campaign elements", href: "/product-management" },
    { label: "Products", href: "/product-management" },
    {
      label: isEditMode ? formData.name || "Edit Product" : "New product",
      href: "#",
    },
  ];

  const MAX_VISIBLE_KEYWORDS = 10;

  return (
    <div className="container">
      <div className="wrap">
        <Breadcrumb breadcrumbs={breadcrumbs} activeIndex={2} />

        {isEditMode && (productsLoading || !isFormReady) ? (
          <div className="product-detail-page">
            <p>Loading product data...</p>
          </div>
        ) : (
          <div className="product-detail-page">
            <div className="product-detail-section">
              <h3 className="section-title">Product information</h3>
              <JsonFormsWrapper
                jsonschema={productFormSchema}
                uischema={productInfoUISchema}
                data={formData}
                onChange={handleFormChange}
                config={jsonFormsConfig}
              />
            </div>

            {/* Active/Inactive Toggle */}
            <div className="product-status-section">
              <Toggle
                checked={isActive}
                onUpdate={(checked) => setIsActive(checked)}
                value="Status"
                // label={isActive ? "Active" : "Inactive"}
                size="medium"
              />
            </div>

            {/* Product Group Section */}
            <div className="product-group-section">
              <h3 className="section-title">Product Group</h3>
              <Dropdown
                childList={productGroups}
                selection="multiple"
                selectedValue={selectedProductGroups}
                setSelectedValue={handleProductGroupChange}
                placeholder="Select product groups"
                size="medium"
              />
            </div>

            {/* Keywords Section */}
            <div className="keywords-section">
              <h3 className="keywords-title">Keywords</h3>

              {keywordGroupsLoading && (
                <p style={{ color: "#666", fontSize: "0.875rem" }}>
                  Loading keyword groups...
                </p>
              )}

              {keywordGroups.map((kg) => (
                <KeywordGroupCard
                  key={kg.id || kg.tempId}
                  keywordGroup={kg}
                  maxVisible={MAX_VISIBLE_KEYWORDS}
                  onEdit={handleEditKeywordGroup}
                  onDelete={handleDeleteKeywordGroup}
                />
              ))}

              <div
                className="add-keyword-group-card"
                onClick={handleAddKeywordGroup}
              >
                <Icon icon="add_circle" className="add-icon" />
                <div className="add-text">
                  <h4>Add keywords group</h4>
                </div>
              </div>
            </div>

            <div className="product-detail-actions">
              <Button
                text="Cancel"
                variant="secondary"
                size="medium"
                onClick={handleCancel}
                disabled={isSubmitting}
              />
              <Button
                text="Save"
                variant="primary"
                size="medium"
                onClick={handleSave}
                disabled={isSubmitting}
              />
            </div>
          </div>
        )}

        <ToastNotification {...toast} handleClose={closeToast} />

        <KeywordGroupEditModal
          isOpen={showKeywordModal}
          keywordGroup={editingKeywordGroup}
          onClose={() => {
            setShowKeywordModal(false);
            setEditingKeywordGroup(null);
          }}
          onSave={handleSaveKeywordGroup}
        />
      </div>
    </div>
  );
}

// Keyword Group Card sub-component
function KeywordGroupCard({ keywordGroup, maxVisible, onEdit, onDelete }) {
  const [showAll, setShowAll] = useState(false);
  const keywords = keywordGroup.keywords || [];
  const visibleKeywords = showAll ? keywords : keywords.slice(0, maxVisible);
  const hasMore = keywords.length > maxVisible;

  return (
    <div className="keyword-group-card">
      <div className="keyword-group-header">
        <h4 className="keyword-group-name">{keywordGroup.name}</h4>
        <div className="keyword-group-actions">
          <Icon
            icon="edit"
            style={{ cursor: "pointer", color: "#007bff", fontSize: "18px" }}
            onClick={() => onEdit(keywordGroup)}
          />
          <Icon
            icon="delete"
            style={{ cursor: "pointer", color: "#dc3545", fontSize: "18px" }}
            onClick={() => onDelete(keywordGroup)}
          />
        </div>
      </div>
      <div className="keyword-tags">
        {visibleKeywords.map((keyword, index) => (
          <Tag
            key={`${keyword}-${index}`}
            text={keyword}
            color="Gray-100"
            isCopyable={false}
            isRemovable={false}
            size="small"
          />
        ))}
        {hasMore && !showAll && (
          <button className="show-all-btn" onClick={() => setShowAll(true)}>
            And {keywords.length - maxVisible} more keywords
          </button>
        )}
        {showAll && hasMore && (
          <button className="show-all-btn" onClick={() => setShowAll(false)}>
            Show less
          </button>
        )}
      </div>
    </div>
  );
}
